import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { trackEvent } from '@/lib/analytics';
import RoughAnnotation from '@/components/common/RoughAnnotation';
import RoughButton from '@/components/common/RoughButton';
import { getGsap } from '@/lib/gsap';

const SPILL_WORDS = [
  // Row 1 — near the photo edge, highest opacity
  { text: '∫ f(x)dx',     style: { '--x': '6%',  '--y': '2%',  '--rot': '-4deg', '--fade': '0.32', '--delay': '0.3s',  '--size': 'clamp(20px, 3vw, 32px)', '--word-color': '#c0392b' } as React.CSSProperties },
  { text: 'assign',        style: { '--x': '28%', '--y': '5%',  '--rot': '3deg',  '--fade': '0.28', '--delay': '0.4s' } as React.CSSProperties },
  { text: 'd/dx',          style: { '--x': '52%', '--y': '3%',  '--rot': '-6deg', '--fade': '0.30', '--delay': '0.35s', '--size': 'clamp(22px, 3vw, 34px)' } as React.CSSProperties },
  { text: 'B+',            style: { '--x': '75%', '--y': '6%',  '--rot': '4deg',  '--fade': '0.25', '--delay': '0.45s', '--word-color': '#c0392b' } as React.CSSProperties },
  { text: '√',             style: { '--x': '92%', '--y': '2%',  '--rot': '-3deg', '--fade': '0.28', '--delay': '0.32s', '--size': 'clamp(24px, 3.5vw, 36px)' } as React.CSSProperties },
  // Row 2
  { text: 'curriculum',    style: { '--x': '10%', '--y': '20%', '--rot': '2deg',  '--fade': '0.22', '--delay': '0.55s' } as React.CSSProperties },
  { text: '✓ graded',     style: { '--x': '38%', '--y': '18%', '--rot': '-3deg', '--fade': '0.24', '--delay': '0.5s', '--word-color': '#c0392b' } as React.CSSProperties },
  { text: 'Σ',             style: { '--x': '62%', '--y': '22%', '--rot': '5deg',  '--fade': '0.26', '--delay': '0.42s', '--size': 'clamp(26px, 3.5vw, 38px)' } as React.CSSProperties },
  { text: 'cos θ',         style: { '--x': '84%', '--y': '19%', '--rot': '-2deg', '--fade': '0.20', '--delay': '0.58s' } as React.CSSProperties },
  // Row 3 — mid zone
  { text: 'homework',      style: { '--x': '5%',  '--y': '38%', '--rot': '-3deg', '--fade': '0.16', '--delay': '0.7s' } as React.CSSProperties },
  { text: '∂y/∂x',        style: { '--x': '30%', '--y': '35%', '--rot': '4deg',  '--fade': '0.18', '--delay': '0.65s', '--size': 'clamp(20px, 2.5vw, 30px)', '--word-color': '#c0392b' } as React.CSSProperties },
  { text: 'chapter 4',     style: { '--x': '55%', '--y': '40%', '--rot': '-2deg', '--fade': '0.14', '--delay': '0.72s' } as React.CSSProperties },
  { text: 'formula',       style: { '--x': '78%', '--y': '36%', '--rot': '3deg',  '--fade': '0.16', '--delay': '0.68s' } as React.CSSProperties },
  // Row 4 — fading out
  { text: 'diagnostics',   style: { '--x': '12%', '--y': '55%', '--rot': '-2deg', '--fade': '0.10', '--delay': '0.85s' } as React.CSSProperties },
  { text: 'mastery',       style: { '--x': '40%', '--y': '52%', '--rot': '4deg',  '--fade': '0.09', '--delay': '0.82s' } as React.CSSProperties },
  { text: 'step-by-step',  style: { '--x': '65%', '--y': '56%', '--rot': '-4deg', '--fade': '0.08', '--delay': '0.88s' } as React.CSSProperties },
  { text: 'concept node',  style: { '--x': '88%', '--y': '53%', '--rot': '2deg',  '--fade': '0.10', '--delay': '0.8s' } as React.CSSProperties },
  // Row 5 — barely visible
  { text: 'practice',      style: { '--x': '18%', '--y': '72%', '--rot': '3deg',  '--fade': '0.05', '--delay': '1.0s' } as React.CSSProperties },
  { text: 'review',        style: { '--x': '48%', '--y': '70%', '--rot': '-3deg', '--fade': '0.05', '--delay': '1.05s' } as React.CSSProperties },
  { text: '100%',          style: { '--x': '76%', '--y': '74%', '--rot': '2deg',  '--fade': '0.04', '--delay': '1.1s', '--word-color': '#c0392b' } as React.CSSProperties },
];

// Gray pencil underline SVG (same path as FinalCTA, different color)
const PencilUnderline: React.FC = () => (
  <svg
    className="hero-pencil-underline"
    viewBox="0 0 280 12"
    fill="none"
    preserveAspectRatio="none"
  >
    <path
      d="M2,6 Q40,3 80,7 T160,5 T240,7 T278,5"
      stroke="#9e9e9e"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
      strokeOpacity="0.7"
    />
  </svg>
);

const SCALE_MIN = 0.82;
const SCALE_MAX = 1;

const ROTATE_WORDS = ['Teachers', 'Tutors', 'Schools', 'Coaching Centers'];

const Hero: React.FC = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [shouldLoadHeroVideo, setShouldLoadHeroVideo] = useState(false);
  const videoWrapRef = useRef<HTMLDivElement>(null);
  const demoRef = useRef<HTMLDivElement>(null);
  const deskRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReducedMotion(mq.matches);
    update();

    if (mq.addEventListener) mq.addEventListener('change', update);
    // @ts-ignore - Safari
    else mq.addListener(update);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      // @ts-ignore - Safari
      else mq.removeListener(update);
    };
  }, []);

  // Keep the demo video out of the critical path. It starts loading only when
  // the framed demo is close enough to be useful.
  useEffect(() => {
    if (prefersReducedMotion || shouldLoadHeroVideo) return;
    const el = videoWrapRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadHeroVideo(true);
          observer.disconnect();
        }
      },
      { rootMargin: '250px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [prefersReducedMotion, shouldLoadHeroVideo]);

  // Scroll-driven grow effect — writes directly to DOM to avoid re-renders
  useEffect(() => {
    const el = videoWrapRef.current;
    const demo = demoRef.current;
    if (!el || !demo || prefersReducedMotion) {
      if (demo) demo.style.transform = `scale(${SCALE_MAX})`;
      return;
    }

    const isMobile = window.innerWidth < 768;
    const scaleMin = isMobile ? 0.92 : SCALE_MIN;
    const spillEl = document.querySelector('.hero-spill') as HTMLElement;

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const vh = window.innerHeight;
        // progress 0 → 1 as element moves from bottom of viewport to center
        const rawProgress = 1 - (rect.top / vh);
        const progress = Math.max(0, Math.min(1, rawProgress));
        const scale = scaleMin + (SCALE_MAX - scaleMin) * progress;
        demo.style.transform = `scale(${scale})`;

        // Mobile parallax for spill words
        if (isMobile && spillEl) {
          const scrollY = window.scrollY;
          spillEl.style.transform = `translateY(${scrollY * 0.3}px)`;
        }

        ticking = false;
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (spillEl) spillEl.style.transform = '';
    };
  }, [prefersReducedMotion]);

  // Load GSAP only after the visitor starts scrolling; the parallax is purely
  // decorative and should not compete with the first paint.
  useEffect(() => {
    if (prefersReducedMotion) return;
    const desk = deskRef.current;
    if (!desk) return;

    let ctx: { revert: () => void } | null = null;
    let cancelled = false;
    let loaded = false;

    const setupParallax = () => {
      if (loaded || cancelled) return;
      loaded = true;
      getGsap().then(({ gsap }) => {
        if (cancelled) return;
        ctx = gsap.context(() => {
          gsap.to(desk, {
            yPercent: 15,
            ease: 'none',
            scrollTrigger: {
              trigger: desk.closest('section'),
              start: 'top top',
              end: 'bottom top',
              scrub: true,
            },
          });
        });
      });
    };

    if (window.scrollY > 24) {
      setupParallax();
    } else {
      window.addEventListener('scroll', setupParallax, { once: true, passive: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('scroll', setupParallax);
      ctx?.revert();
    };
  }, [prefersReducedMotion]);

  return (
    <section className="hero-section relative flex flex-col items-center pb-20 px-6 overflow-hidden">
      {/* Decorative Layer 1: Desk photo texture — absolute, zero layout impact, GSAP parallax */}
      <div className="hero-desk" aria-hidden="true" ref={deskRef}>
        <Image
          src="/hero-desk.png"
          alt=""
          className="hero-desk__img"
          width={904}
          height={904}
          sizes="110vw"
          loading="lazy"
          decoding="async"
          fetchPriority="low"
          placeholder="blur"
          blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOTA0IiBoZWlnaHQ9IjkwNCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmNGY5Ii8+PC9zdmc+"
        />
      </div>

      {/* Decorative Layer 2: Spilling handwritten words — absolute, fading */}
      <div className="hero-spill" aria-hidden="true">
        {SPILL_WORDS.map((word) => (
          <span key={word.text} className="hero-spill__word" style={word.style}>
            {word.text}
          </span>
        ))}
      </div>


      <div className="hero-section__inner relative z-[2] max-w-[1250px] mx-auto w-full">
        {/* Centered Text Block */}
        <div className="hero-section__copy-wrap flex items-center justify-center pt-20 sm:pt-10 md:pt-0 pb-14 sm:pb-10 md:pb-0" style={{ minHeight: 'calc(100vh - 160px)' }}>
        <div className="hero-section__copy text-center max-w-[1100px] mx-auto flex flex-col justify-center animate-in fade-in slide-in-from-bottom duration-700 px-2 sm:px-0">
          <div className="space-y-5 sm:space-y-7">
            <h1 className="text-[clamp(30px,8vw,40px)] sm:text-[clamp(30px,6.2vw,44px)] md:text-[80px] font-semibold tracking-tight leading-[1.15] text-[var(--color-text)] font-heading break-words hero-h1">
              Stop grading papers.<br />
              <RoughAnnotation
                type="highlight"
                color="rgba(255, 241, 118, 0.35)"
                strokeWidth={1}
                padding={[2, 4]}
                animationDuration={1000}
                delay={1200}
                triggerOnScroll={false}
                show={true}
              >
                Start understanding students.
              </RoughAnnotation>
            </h1>
            <div className="hero-rotating-line text-[clamp(18px,4.5vw,24px)] sm:text-[clamp(20px,4vw,28px)] md:text-[42px] font-semibold text-[var(--color-primary)] leading-[1.3] sm:leading-[1.2]" style={{ fontFamily: 'var(--font-heading)' }}>
              Learning intelligence for{' '}
              <span className="relative inline-block">
                <span className="hero-rotate-wrap">
                  <span className="hero-rotate-words">
                    {ROTATE_WORDS.map((word) => (
                      <span key={word} className="hero-rotate-word">{word}</span>
                    ))}
                    <span className="hero-rotate-word" aria-hidden="true">{ROTATE_WORDS[0]}</span>
                  </span>
                </span>
                <PencilUnderline />
                {/* Teacher's green pen grade mark */}
                <div className="hero-grade-mark" aria-hidden="true">
                  <span className="hero-grade-text">A+</span>
                  <svg className="hero-grade-circle" viewBox="0 0 60 50" width="60" height="50" fill="none">
                    <ellipse cx="30" cy="25" rx="26" ry="20" stroke="rgba(50,151,87,0.25)" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="3,3" />
                  </svg>
                </div>
              </span>
            </div>
            <div className="hero-cta-group flex gap-4 justify-center flex-wrap pt-2">
              <RoughButton
                variant="primary"
                href="/book-demo"
                onClick={() => trackEvent('book_demo_click', 'CTA', 'hero')}
                seed={2}
              >
                Book a Demo →
              </RoughButton>
              <RoughButton
                variant="secondary"
                href="#how-it-works"
                onClick={() => trackEvent('hero_cta_how_it_works')}
                seed={7}
              >
                See How It Works
              </RoughButton>
            </div>
          </div>
        </div>
        </div>

        {/* Video Demo */}
        <div
          ref={videoWrapRef}
          className="animate-in fade-in duration-1000 mt-6 md:mt-8"
        >
          <div
            ref={demoRef}
            className="hero-demo"
          >
            <div className="hero-video-slide">
              <div className="hero-video-slide__glow" />
              <div className="hero-video-slide__browser">
                <div className="hero-video-slide__titlebar">
                  <svg className="hero-video-slide__dots" width="54" height="14" viewBox="0 0 54 14" fill="none">
                    <circle cx="7" cy="7" r="5.5" fill="#FF5F57" stroke="#E0443E" strokeWidth="0.5" />
                    <circle cx="27" cy="7" r="5.5" fill="#FEBC2E" stroke="#DEA123" strokeWidth="0.5" />
                    <circle cx="47" cy="7" r="5.5" fill="#28C840" stroke="#1AAB29" strokeWidth="0.5" />
                  </svg>
                  <span className="hero-video-slide__url">intelgrader.com</span>
                </div>
                <div className="video-skeleton">
                  <video
                    className="hero-video-slide__video"
                    src={shouldLoadHeroVideo ? '/hero-demo.mp4' : undefined}
                    autoPlay={shouldLoadHeroVideo && !prefersReducedMotion}
                    muted
                    loop
                    playsInline
                    preload={shouldLoadHeroVideo ? 'auto' : 'none'}
                    width="1280"
                    height="720"
                    onCanPlay={(e) => (e.currentTarget.parentElement as HTMLElement)?.classList.add('is-loaded')}
                  />
                </div>
                <div className="hero-video-slide__vignette" />
              </div>
            </div>
          </div>

          {/* Caption */}
          <div className="hero-demo__caption">
            <div
              className="hero-demo__caption-feature"
              style={{ fontFamily: 'var(--font-caveat)' }}
            >
              Live platform demo
            </div>
            <div className="hero-demo__caption-title">
              See learning intelligence in action
            </div>
            <div className="hero-demo__caption-sub">
              Your curriculum, mapped. Every concept gap, surfaced.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
